import TopSortCardsBadge from '@/src/components/common/srp/topSortCardsLabel/topSortCardsLabel'
import YellowBannerPromo from '@/src/components/common/srp/yellowBannerPromo'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'
import { useGetSortedTopCards } from '@/src/hooks/srp/getGetSortedTopCards'
import { useRTSolutionCardStatesManager } from '@/src/hooks/srp/RTSolutionView/RTSolutionCardStatesManager'
import { FlightCard } from '@/src/types/flight'
import { Box, Button, Card, Collapse, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { FC, useCallback } from 'react'
import ReactList from 'react-list'
import RtSolutionCard from './rtSolutionCard'

interface RtSolutionCardWrapperProps {
    samePriceCards: FlightCard[]
    onSelectCard?: (card: FlightCard) => void
    onViewDetailsClick?: (card: FlightCard[], showBaggage?: boolean) => void
}

const RtSolutionCardWrapper: FC<RtSolutionCardWrapperProps> = ({
    samePriceCards,
    onSelectCard,
    onViewDetailsClick
}) => {
    const { expandSamePriceCard, handleExpandSamePriceCard, mainCard, extraCards } =
        useRTSolutionCardStatesManager(samePriceCards)
    const tFlightDetails = useTranslations('srpFlightDetails')
    const topSortedCards = useGetSortedTopCards()
    const sortedTopCard = topSortedCards.find(sortOption => sortOption?.card?.id === mainCard.id)
    const renderSamePriceFlightCard = useCallback(
        (index: number) => {
            const card = extraCards[index]

            const sortedExtraTopCard = topSortedCards.find(
                sortOption => sortOption?.card?.id === card.id
            )

            if (sortedExtraTopCard) {
                return (
                    <ErrorBoundaryWrapper
                        identifier="rt-solution-same-price-card-PWA-RT-solution-flow"
                        key={card.id}
                        fallback={null}
                    >
                        <TopSortCardsBadge
                            label={sortedExtraTopCard.label}
                            isActive={!!sortedExtraTopCard.isActive}
                            sortOptionId={sortedExtraTopCard.id}
                            isMobile={true}
                            isCombined={sortedExtraTopCard.isCombined}
                        >
                            <Card
                                sx={{
                                    border: '1px solid #eee',
                                    borderRadius: 2,
                                    background: '#fff',
                                    mb: 2
                                }}
                            >
                                <RtSolutionCard
                                    card={card}
                                    onSelectCard={onSelectCard}
                                    onViewDetailsClick={onViewDetailsClick}
                                />
                            </Card>
                        </TopSortCardsBadge>
                    </ErrorBoundaryWrapper>
                )
            }

            return (
                <ErrorBoundaryWrapper
                    identifier="rt-solution-same-price-card-PWA-RT-solution-flow"
                    key={card.id}
                    fallback={null}
                >
                    <Card
                        sx={{
                            border: '1px solid #eee',
                            borderRadius: 2,
                            background: '#fff',
                            mb: 2
                        }}
                    >
                        <RtSolutionCard
                            card={card}
                            onSelectCard={onSelectCard}
                            onViewDetailsClick={onViewDetailsClick}
                        />
                    </Card>
                </ErrorBoundaryWrapper>
            )
        },
        [extraCards, onSelectCard, onViewDetailsClick, topSortedCards]
    )


    if (sortedTopCard) {
        return <Box mb={1}> <TopSortCardsBadge
            label={sortedTopCard.label}
            isActive={!!sortedTopCard.isActive}
            sortOptionId={sortedTopCard.id}
            isMobile={true}
            isCombined={sortedTopCard.isCombined}
        >
            <Card
                sx={{
                    border: '1px solid #eee',
                    borderRadius: 2,
                    background: '#fff',
                    transition: 'border 0.2s, padding 0.2s',
                    position: 'relative'
                }}
            >
                <Box height={"15px"}></Box>
                <YellowBannerPromo promos={sortedTopCard?.card?.promos} isMobile />
                {!!sortedTopCard?.card?.promos?.length && <Box height={"15px"}></Box>}
                <Box
                    sx={{
                        pt: (sortedTopCard?.card?.promos?.length || 0) > 0 ? 1 : 0
                    }}
                >
                    <RtSolutionCard
                        card={sortedTopCard?.card!}
                        onSelectCard={onSelectCard}
                        onViewDetailsClick={onViewDetailsClick}
                    />
                </Box>
            </Card>
        </TopSortCardsBadge>
        </Box>
    }

    return (
        <Box sx={{ mb: 1 }}>
            {/* Main Card */}
            <Card
                onClick={handleExpandSamePriceCard}
                sx={{
                    border: '1px solid #eee',
                    borderRadius: 2,
                    background: '#fff',
                    cursor: extraCards.length > 0 ? 'pointerd' : 'default',
                    transition: 'border 0.2s, padding 0.2s',
                    position: 'relative'
                }}
            >
                <YellowBannerPromo promos={mainCard.promos} isMobile />
                <Box sx={{ pt: mainCard.promos?.length > 0 ? 1 : 0 }}>
                    <RtSolutionCard
                        card={mainCard}
                        onSelectCard={onSelectCard}
                        onViewDetailsClick={onViewDetailsClick}
                    />
                </Box>
            </Card>

            {/* Expanded section: label, hide button, and extra cards */}

            <Collapse in={expandSamePriceCard && extraCards.length > 0} unmountOnExit timeout="auto">
                <Box sx={{ background: '#fafbfc', borderRadius: 2, p: 1, mt: 1, px: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {tFlightDetails('samePriceFlights')}
                        </Typography>
                        <Button
                            size="small"
                            onClick={handleExpandSamePriceCard}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            {tFlightDetails('hideMoreOption')}
                        </Button>
                    </Box>
                    <ReactList length={extraCards.length} itemRenderer={renderSamePriceFlightCard} />
                </Box>
            </Collapse>

            {/* Show the 'N more at same price' bar if not expanded */}
            {!expandSamePriceCard && extraCards.length > 0 && (
                <Box
                    onClick={handleExpandSamePriceCard}
                    sx={{
                        cursor: 'pointer',
                        width: '90%',
                        height: '23px',
                        mx: 'auto',
                        borderBottomLeftRadius: '12px',
                        borderBottomRightRadius: '12px',
                        background: '#F3F3F3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 600,
                        fontSize: '12px',
                        color: '#3B5B98'
                    }}
                >
                    +{extraCards.length} {tFlightDetails('moreOptionAvailiable')}
                </Box>
            )}
        </Box>
    )
}

export default RtSolutionCardWrapper
